/**
 * Outbox v2 entry schema and its validator (ATL-1445, write cutover stage 3, plan §2).
 *
 * An entry is ONE backend-first write the device still owes the backend: the request exactly as
 * it will be sent, the document it is keyed on, and its delivery state. Once an organization is
 * Supabase-master the entry is the ONLY copy of that write anywhere, so the rules below are about
 * never losing it and never replaying something different from what the writer produced.
 *
 * Everything read back from disk or IndexedDB is DATA, not a trusted value: an older or newer
 * bundle may have written it, a power loss may have truncated it, and a bug may have mangled it.
 * `validateStoredEntry` is the only way a stored record becomes an `OutboxEntryV2`; a record that
 * fails it is surfaced as corrupt (held, visible) by the store — never repaired by guessing and
 * never deleted.
 *
 * Deliberate strictness, each for a reason that is about the books:
 *  - UNKNOWN FIELDS ARE REFUSED, not ignored. A later bundle that adds a field whose meaning an
 *    older reader must honour (a replay precondition, as ATL-1207's `deletedFrom` was for v1)
 *    would otherwise be replayed WITHOUT it by a rolled-back bundle. Adding a field therefore
 *    means bumping `v`; an older bundle then holds the entry instead of mis-sending it.
 *  - The request never carries headers. `TypedApiClient.replayRequest` authenticates at send
 *    time; a persisted bearer token would be both stale and a credential on disk (same rule as
 *    `attachReplayableRequest.js`).
 *  - The URL is a path relative to the API base (`/sales`, never `https://host/...`). A persisted
 *    absolute URL pins a backend host into the queue, which is how the v1 outbox kept writing to
 *    the dead Railway host after the move to Cloud Run (ATL-902).
 *  - Only plain JSON is accepted. `NaN`, `Infinity`, a `Date`, a class instance or an `undefined`
 *    array slot would be COERCED by `JSON.stringify` (to `null`, a string, `{}`), i.e. the store
 *    would silently persist a different value from the one the writer produced — for a money
 *    field, a believable wrong number. They are refused instead. `undefined` as an object property
 *    value is accepted and omitted, because that is exactly what the wire does with it.
 */
import type { ReplayableRequestConfig } from '@atlas/api-client'

// ============================================================================
// Types
// ============================================================================

export type JsonPrimitive = string | number | boolean | null
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue }
export type JsonObject = { [key: string]: JsonValue }

export const OUTBOX_ENTRY_VERSION = 2 as const

/** Writes only — the queue never holds a read. */
export const OUTBOX_REQUEST_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'] as const
export type OutboxRequestMethod = (typeof OUTBOX_REQUEST_METHODS)[number]

export const OUTBOX_WRITE_KINDS = ['create', 'update', 'delete', 'delta', 'absolute'] as const
export type OutboxWriteKind = (typeof OUTBOX_WRITE_KINDS)[number]

/**
 * Where the entry came from: a live write in Supabase mode, a v1 dual-write entry copied at the
 * flip (plan §2 "Migrating v1 entries"), or a Firestore-first mirror the backend refused with 409
 * `WRITE_MASTER_SUPABASE` and the app re-enqueued (plan §4).
 */
export const OUTBOX_ENTRY_ORIGINS = ['live', 'v1-migrated', 'mirror-409-converted'] as const
export type OutboxEntryOrigin = (typeof OUTBOX_ENTRY_ORIGINS)[number]

/**
 * `pending` — waiting to be sent. `in-flight` — handed to the transport; it may have committed
 * server-side, so nothing may purge it. `held` — refused deterministically; only a person resolves
 * it (invariant 14).
 */
export const OUTBOX_ENTRY_STATES = ['pending', 'in-flight', 'held'] as const
export type OutboxEntryState = (typeof OUTBOX_ENTRY_STATES)[number]

/**
 * The request as it will be re-issued through `TypedApiClient.replayRequest`. Structurally a
 * `ReplayableRequestConfig` (asserted below), narrowed to writes and to JSON.
 */
export interface OutboxRequest {
  method: OutboxRequestMethod
  /** Path relative to the API base, starting with a single `/`. */
  url: string
  data?: JsonValue
  params?: JsonObject
}

export interface OutboxLastError {
  /** HTTP status, or `null` for a transport failure that produced none. */
  status: number | null
  /** Backend error code (e.g. `WRITE_MASTER_SUPABASE`) when the response carried one. */
  code: string | null
}

export interface OutboxAdoption {
  /** The `installId` the entry carried before the operator adopted it. */
  fromInstallId: string
  /** Epoch ms of the adoption. */
  at: number
  /** Who adopted it (operator identity, as the operator tool knows it). */
  actor: string
  /** Why — the check the operator made against the books. */
  reason: string
}

/** What an operator must state to adopt entries; stored verbatim in each adoption record. */
export interface OutboxAdoptionAudit {
  actor: string
  reason: string
}

const MAX_AUDIT_ACTOR_LENGTH = 200
const MAX_AUDIT_REASON_LENGTH = 2000

/** `null` when the audit fields are usable, else why not. */
export function adoptionAuditProblem(value: unknown): string | null {
  if (!isPlainObject(value)) return 'audit: not an object'
  const extra = unknownField(value, AUDIT_FIELDS)
  if (extra !== null) return `audit.${extra}: unknown field`
  if (typeof value.actor !== 'string' || value.actor.trim().length === 0 || value.actor.length > MAX_AUDIT_ACTOR_LENGTH) {
    return 'audit.actor: required, a non-empty string'
  }
  if (
    typeof value.reason !== 'string' ||
    value.reason.trim().length === 0 ||
    value.reason.length > MAX_AUDIT_REASON_LENGTH
  ) {
    return 'audit.reason: required, a non-empty string'
  }
  return null
}

export interface OutboxEntryV2 {
  v: typeof OUTBOX_ENTRY_VERSION
  /** Unique within the organization; the entry's identity (the v1 id for `v1-migrated`). */
  entryId: string
  /**
   * Position in the organization's FIFO queue, assigned by the store. Strictly increasing within
   * an organization for the life of a store instance. It orders entries; it does not identify
   * them — `entryId` does (the native engine may reuse the seq of a removed TAIL entry after a
   * restart, the web engine never does).
   */
  seq: number
  /**
   * The install that queued it, stamped by the store (review S3). A queue restored from a phone
   * backup onto a fresh install carries the OLD install's id, so its entries — some of which that
   * install already delivered — are distinguishable from this install's own (`foreignInstall`).
   */
  installId: string
  /**
   * Audit trail of operator `adoptForeign` calls, oldest first: each re-stamped `installId` and
   * when. Empty for every entry the drain never had to be told about. Store-assigned.
   */
  adoptions: OutboxAdoption[]
  orgFirestoreId: string
  /** `module.op` label, the same one today's dual-write uses (telemetry continuity). */
  label: string
  /** Device-minted Firestore id — the backend's per-organization idempotency key (invariant 13). */
  documentId: string
  /** Other documents this write references (a sale's client, a return's sale). */
  relatedIds: string[]
  kind: OutboxWriteKind
  request: OutboxRequest
  /** Local-view effects (plan §3); opaque to the store. */
  effects: JsonValue | null
  /** Epoch ms when the writer queued it (the device's checkout time, invariant 7). */
  enqueuedAt: number
  /** EAS update id / bundle identity of the writer, `null` when unknown. */
  bundleId: string | null
  origin: OutboxEntryOrigin
  state: OutboxEntryState
  attempts: number
  lastError: OutboxLastError | null
  /** Epoch ms when the entry became `held`; required while `state === 'held'`. */
  heldSince: number | null
  /** Epoch ms before which the drainer must not retry it. */
  nextAttemptAt: number
}

/** What a writer hands to `append`: everything but the fields the store assigns. */
export type NewOutboxEntry = Omit<OutboxEntryV2, 'seq' | 'installId' | 'adoptions'>

/**
 * The resource a write addresses: the module of its `module.op` label (`sales.update` → `sales`).
 * A delete supersedes queued writes only within its own resource (review N5): document ids are
 * Firestore ids, and ids chosen by the app (dates, `<storeId>_<priceType>`) can repeat across
 * collections.
 */
export function resourceOf(entry: Pick<OutboxEntryV2, 'label'>): string {
  const dot = entry.label.indexOf('.')
  return dot === -1 ? entry.label : entry.label.slice(0, dot)
}

/** The only fields `update` may change; identity, payload and provenance are immutable. */
export const OUTBOX_MUTABLE_FIELDS = ['state', 'attempts', 'lastError', 'heldSince', 'nextAttemptAt'] as const
export type OutboxMutableField = (typeof OUTBOX_MUTABLE_FIELDS)[number]
export type OutboxEntryPatch = Partial<Pick<OutboxEntryV2, OutboxMutableField>>

// Compile-time proof that a stored request can be handed to `replayRequest` unchanged.
type AssertAssignable<T extends ReplayableRequestConfig> = T
export type OutboxRequestIsReplayable = AssertAssignable<OutboxRequest>

// ============================================================================
// Identifiers used in storage keys
// ============================================================================

/**
 * `orgFirestoreId` and `entryId` become path components on native (`<orgFsId>/<seq>-<entryId>.json`),
 * so they are restricted to characters that can never traverse or split a path. Firestore auto ids
 * are `[A-Za-z0-9]{20}`; v1 outbox ids are `[a-z0-9-]`.
 */
const STORAGE_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/

export function isStorageSafeId(value: unknown): value is string {
  return typeof value === 'string' && STORAGE_ID_PATTERN.test(value)
}

// ============================================================================
// Validation
// ============================================================================

export type OutboxValidation<T> = { ok: true; value: T } | { ok: false; reason: string }

const ENTRY_FIELDS: ReadonlySet<string> = new Set([
  'v',
  'entryId',
  'seq',
  'installId',
  'adoptions',
  'orgFirestoreId',
  'label',
  'documentId',
  'relatedIds',
  'kind',
  'request',
  'effects',
  'enqueuedAt',
  'bundleId',
  'origin',
  'state',
  'attempts',
  'lastError',
  'heldSince',
  'nextAttemptAt',
])
const REQUEST_FIELDS: ReadonlySet<string> = new Set(['method', 'url', 'data', 'params'])
const LAST_ERROR_FIELDS: ReadonlySet<string> = new Set(['status', 'code'])

/** Deeper than any real DTO; bounds the recursion on hostile input. */
const MAX_JSON_DEPTH = 64
/** Firestore's own document-id limit is 1,500 bytes. */
const MAX_DOCUMENT_ID_LENGTH = 1500
const MAX_LABEL_LENGTH = 200

/**
 * A plain object from ANY realm: its prototype is null or some realm's `Object.prototype` (whose
 * own prototype is null). IndexedDB hands back structured clones, which need not share this
 * realm's `Object.prototype`; a class instance or a `Date` has a prototype one level deeper.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false
  const proto: unknown = Object.getPrototypeOf(value)
  return proto === null || Object.getPrototypeOf(proto) === null
}

/**
 * `null` when `value` is plain JSON (see the module docblock for what that admits), otherwise the
 * path and reason of the first offending value.
 */
export function findNonJson(value: unknown, path = '$', depth = 0): string | null {
  if (depth > MAX_JSON_DEPTH) return `${path}: nested deeper than ${MAX_JSON_DEPTH}`
  if (value === null) return null
  switch (typeof value) {
    case 'string':
    case 'boolean':
      return null
    case 'number':
      return Number.isFinite(value) ? null : `${path}: non-finite number`
    case 'object':
      break
    default:
      return `${path}: ${typeof value} is not JSON`
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      // A hole or `undefined` slot would be written as `null` — a changed value.
      if (!(i in value) || value[i] === undefined) return `${path}[${i}]: undefined array slot`
      const found = findNonJson(value[i], `${path}[${i}]`, depth + 1)
      if (found) return found
    }
    return null
  }
  if (!isPlainObject(value)) return `${path}: not a plain object`
  for (const key of Object.keys(value)) {
    const child = value[key]
    if (child === undefined) continue // omitted, exactly as the wire omits it
    const found = findNonJson(child, `${path}.${key}`, depth + 1)
    if (found) return found
  }
  return null
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
}

function isTimestamp(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function oneOf<T extends string>(allowed: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
}

function unknownField(value: Record<string, unknown>, allowed: ReadonlySet<string>): string | null {
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) return key
  }
  return null
}

function validateRequest(value: unknown): string | null {
  if (!isPlainObject(value)) return 'request: not an object'
  const extra = unknownField(value, REQUEST_FIELDS)
  if (extra !== null) {
    return extra === 'headers'
      ? 'request.headers: headers are never persisted'
      : `request.${extra}: unknown field`
  }
  if (!oneOf(OUTBOX_REQUEST_METHODS, value.method)) return 'request.method: not a write method'
  if (typeof value.url !== 'string' || !value.url.startsWith('/') || value.url.startsWith('//')) {
    return 'request.url: must be a path relative to the API base'
  }
  if (value.data !== undefined) {
    const found = findNonJson(value.data, 'request.data')
    if (found) return found
  }
  if (value.params !== undefined) {
    if (!isPlainObject(value.params)) return 'request.params: not an object'
    const found = findNonJson(value.params, 'request.params')
    if (found) return found
  }
  return null
}

function validateLastError(value: unknown): string | null {
  if (value === null) return null
  if (!isPlainObject(value)) return 'lastError: not an object'
  const extra = unknownField(value, LAST_ERROR_FIELDS)
  if (extra !== null) return `lastError.${extra}: unknown field`
  if (value.status !== null && !(typeof value.status === 'number' && Number.isInteger(value.status))) {
    return 'lastError.status: not an integer or null'
  }
  if (value.code !== null && typeof value.code !== 'string') return 'lastError.code: not a string or null'
  return null
}

/** `stored`: `seq`, `installId`, `adoptions` present (the store assigned them). `new`: all absent. */
const AUDIT_FIELDS: ReadonlySet<string> = new Set(['actor', 'reason'])
const ADOPTION_FIELDS: ReadonlySet<string> = new Set(['fromInstallId', 'at', 'actor', 'reason'])

function validateAdoptions(value: unknown): string | null {
  if (!Array.isArray(value)) return 'adoptions: not an array'
  for (let i = 0; i < value.length; i++) {
    const adoption: unknown = value[i]
    if (!isPlainObject(adoption)) return `adoptions[${i}]: not an object`
    const extra = unknownField(adoption, ADOPTION_FIELDS)
    if (extra !== null) return `adoptions[${i}].${extra}: unknown field`
    if (!isStorageSafeId(adoption.fromInstallId)) return `adoptions[${i}].fromInstallId: not a storage-safe id`
    if (!isTimestamp(adoption.at)) return `adoptions[${i}].at: not an epoch-ms timestamp`
    const auditProblem = adoptionAuditProblem({ actor: adoption.actor, reason: adoption.reason })
    if (auditProblem) return `adoptions[${i}].${auditProblem.slice('audit.'.length)}`
  }
  return null
}

function validateFields(value: unknown, mode: 'stored' | 'new'): string | null {
  if (!isPlainObject(value)) return 'entry: not an object'
  const extra = unknownField(value, ENTRY_FIELDS)
  if (extra !== null) return `${extra}: unknown field (a new field requires a new version)`
  if (value.v !== OUTBOX_ENTRY_VERSION) return `v: expected ${OUTBOX_ENTRY_VERSION}`
  if (!isStorageSafeId(value.entryId)) return 'entryId: not a storage-safe id'
  if (mode === 'stored') {
    if (!(isNonNegativeInteger(value.seq) && value.seq > 0)) return 'seq: not a positive integer'
    if (!isStorageSafeId(value.installId)) return 'installId: not a storage-safe id'
    const adoptionsProblem = validateAdoptions(value.adoptions)
    if (adoptionsProblem) return adoptionsProblem
  } else {
    if (value.seq !== undefined) return 'seq: assigned by the store, must not be supplied'
    if (value.installId !== undefined) return 'installId: assigned by the store, must not be supplied'
    if (value.adoptions !== undefined) return 'adoptions: assigned by the store, must not be supplied'
  }
  if (!isStorageSafeId(value.orgFirestoreId)) return 'orgFirestoreId: not a storage-safe id'
  if (typeof value.label !== 'string' || value.label.length === 0 || value.label.length > MAX_LABEL_LENGTH) {
    return 'label: not a non-empty string'
  }
  if (
    typeof value.documentId !== 'string' ||
    value.documentId.length === 0 ||
    value.documentId.length > MAX_DOCUMENT_ID_LENGTH
  ) {
    return 'documentId: not a non-empty string'
  }
  if (!Array.isArray(value.relatedIds) || !value.relatedIds.every((id) => typeof id === 'string' && id.length > 0)) {
    return 'relatedIds: not an array of non-empty strings'
  }
  if (!oneOf(OUTBOX_WRITE_KINDS, value.kind)) return 'kind: unknown write kind'
  const requestProblem = validateRequest(value.request)
  if (requestProblem) return requestProblem
  // Review N7: a `delete` entry is what `appendDelete` purges on behalf of; a POST labelled as one
  // would let a write that deletes nothing supersede real ones.
  if (value.kind === 'delete' && (value.request as Record<string, unknown>).method !== 'DELETE') {
    return 'kind: a delete must be sent as DELETE'
  }
  if (value.effects === undefined) return 'effects: missing (use null for none)'
  const effectsProblem = findNonJson(value.effects, 'effects')
  if (effectsProblem) return effectsProblem
  if (!isTimestamp(value.enqueuedAt)) return 'enqueuedAt: not an epoch-ms timestamp'
  if (value.bundleId !== null && typeof value.bundleId !== 'string') return 'bundleId: not a string or null'
  if (!oneOf(OUTBOX_ENTRY_ORIGINS, value.origin)) return 'origin: unknown origin'
  if (!oneOf(OUTBOX_ENTRY_STATES, value.state)) return 'state: unknown state'
  if (!isNonNegativeInteger(value.attempts)) return 'attempts: not a non-negative integer'
  const lastErrorProblem = validateLastError(value.lastError)
  if (lastErrorProblem) return lastErrorProblem
  if (value.heldSince !== null && !isTimestamp(value.heldSince)) return 'heldSince: not a timestamp or null'
  if (value.state === 'held' && value.heldSince === null) return 'heldSince: required while held'
  if (!isTimestamp(value.nextAttemptAt)) return 'nextAttemptAt: not an epoch-ms timestamp'
  return null
}

/**
 * Normalise a writer-supplied entry to the exact JSON both engines persist, or say why it cannot
 * be. The result is a fresh object: later mutation of the caller's object cannot reach the store.
 */
export function prepareNewEntry(input: unknown): OutboxValidation<NewOutboxEntry> {
  const problem = validateFields(input, 'new')
  if (problem) return { ok: false, reason: problem }
  return { ok: true, value: JSON.parse(JSON.stringify(input)) as NewOutboxEntry }
}

/** Validate a record read back from storage. Never trusts, never repairs. */
export function validateStoredEntry(value: unknown): OutboxValidation<OutboxEntryV2> {
  const problem = validateFields(value, 'stored')
  if (problem) return { ok: false, reason: problem }
  return { ok: true, value: value as OutboxEntryV2 }
}

/**
 * Apply a patch to a stored entry and validate the result. Only `OUTBOX_MUTABLE_FIELDS` may
 * appear in the patch; anything else is refused rather than dropped.
 */
export function applyEntryPatch(entry: OutboxEntryV2, patch: unknown): OutboxValidation<OutboxEntryV2> {
  if (!isPlainObject(patch)) return { ok: false, reason: 'patch: not an object' }
  const mutable: ReadonlySet<string> = new Set(OUTBOX_MUTABLE_FIELDS)
  for (const key of Object.keys(patch)) {
    if (!mutable.has(key)) return { ok: false, reason: `patch.${key}: immutable or unknown field` }
    if (patch[key] === undefined) return { ok: false, reason: `patch.${key}: undefined (use null to clear)` }
  }
  // Before the JSON round-trip below, which would turn a NaN `heldSince` into a valid-looking null.
  const nonJson = findNonJson(patch, 'patch')
  if (nonJson) return { ok: false, reason: nonJson }
  const merged: unknown = JSON.parse(JSON.stringify({ ...entry, ...patch }))
  return validateStoredEntry(merged)
}
